import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { NodeCollection } from 'graph/NodeCollection';
import { GraphCollection } from './GraphCollection';
import {
  buildAggregatedGraph as AggregatedGraphBuilder,
  AggregatedGraphBuilderNode,
} from 'graph/aggregatedGraphBuilder';
import { BackboneEvents } from 'BackboneEvents';
import { graphUtil as GraphUtil } from 'graph/graphUtil';
import {
  type Edge,
  Node,
  type AggregatedGraphByComponentArgs,
  type AggregatedGraphByWorkspaceArgs,
} from './types';
import { ChildGraphInclusionPolicy } from './ChildGraphInclusionPolicy';
import * as profiling from '@ardoq/profiling';
import { action$, ofType } from '@ardoq/rxbeach';
import {
  notifyFiltersChanged,
  setActivePerspective,
} from 'streams/filters/FilterActions';
import { references$ } from 'streams/references/references$';
import { components$ } from 'streams/components/components$';
import { GraphItemModel } from './GraphItem';
import {
  APIComponentAttributes,
  ArdoqId,
  GroupingRule,
} from '@ardoq/api-types';
import { componentOperations, groupingRuleOperations } from '@ardoq/core';
import { createComponentNode } from './createNode';
import { getComponentTypeInfoFor } from './componentTypeInfo';
import { workspaceChanges$ } from 'streams/context/context$';
import { combineLatest, map } from 'rxjs';
import { once } from 'lodash';
import { componentTypesByWorkspaceId$ } from 'streams/models/componentTypesByWorkspaceId$';
import { referenceTypesByWorkspaceId$ } from 'streams/models/referenceTypesByWorkspaceId$';
import { fields$ } from 'streams/fields/fields$';
import { persistentWorkspaces$ } from 'streams/workspaces/workspaces$';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import {
  EMPTY_GRAPH_MODEL_DATA,
  GraphModelData,
  sortedGraphComponents$,
} from './graphModelData';
import { sort$ } from 'streams/context/sort$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { tags$ } from 'streams/tags/tags$';
import { referenceLabelFilterField$ } from 'streams/filters/referenceLabelFilterField$';
import { getCurrentIncludedInContext } from 'streams/filters/includedInContext$';

interface TraverseLogEntry {
  maxIncoming: number;
  maxOutgoing: number;
}
interface EnsureAtLeastOneDegreeForNodeInContextArgs {
  aggNode: Partial<Pick<AggregatedGraphBuilderNode, 'nodes'>>;
  node: Node;
  maxDegrees: number;
}
interface IncludeNodesAndEdgesParam {
  nodeSet: Set<Node<GraphItemModel>>;
  edgeSet: Set<Edge>;
  maxDegrees?: number;
  maxDegreesIncoming: number;
  maxDegreesOutgoing: number;
  getAggregated: boolean;
  traverseLog: Map<string, TraverseLogEntry>;
}
interface AddChildGraphsArgs {
  component?: APIComponentAttributes | null;
  maxDegreesIncoming: number;
  maxDegreesOutgoing: number;
  isProcess?: boolean;
  getAggregated: boolean;
  includeChildGraphs: ChildGraphInclusionPolicy;
  includedNodes: Set<Node<GraphItemModel>>;
  includedEdges: Set<Edge>;
  traverseLog: Map<string, TraverseLogEntry>;
}
const ensureAtLeastOneDegreeForNodeInContext = ({
  aggNode,
  node,
  maxDegrees,
}: EnsureAtLeastOneDegreeForNodeInContextArgs) =>
  maxDegrees > 0
    ? maxDegrees
    : (aggNode?.nodes?.indexOf(node) ?? -1) >= 0
      ? 1
      : 0;

const includeNodeAndParents = (
  nodes: Set<Node<GraphItemModel>>,
  node: Node<GraphItemModel>
) => {
  nodes.add(node);
  if (node.parent) {
    includeNodeAndParents(nodes, node.parent);
  }
};

const emptyGraph = () => ({
  rootNodes: new NodeCollection(),
  edges: new GraphCollection<Edge>(),
  activeNodes: {},
});

class AggregatedGraph extends BackboneEvents {
  private dirty = true;
  private compMap = new Map<string, AggregatedGraphBuilderNode | undefined>();
  private rootNodeCollection = new NodeCollection();
  private edgeMap = new GraphCollection<Edge>();
  private graphModelData: GraphModelData = EMPTY_GRAPH_MODEL_DATA;
  private sortedChildrenByParent: ReadonlyMap<
    ArdoqId,
    readonly APIComponentAttributes[]
  > = new Map();
  private groupingRules: GroupingRule[] = [];
  unthrottledRebuild: VoidFunction;
  constructor() {
    super();

    workspaceChanges$.subscribe(this.invalidateGraph);

    components$.subscribe(this.invalidateGraph);

    action$
      .pipe(ofType(notifyFiltersChanged, setActivePerspective))
      .subscribe(this.invalidateGraph);

    references$.subscribe(this.invalidateGraph);

    groupingRules$.subscribe(this.invalidateGraph);

    sort$.subscribe(this.invalidateGraph);

    locale$.subscribe(this.invalidateGraph);

    combineLatest({
      componentTypesByWorkspaceId: componentTypesByWorkspaceId$,
      referenceTypesByWorkspaceId: referenceTypesByWorkspaceId$,
      fields: fields$,
      tags: tags$,
      workspaces: persistentWorkspaces$,
      orgUsers: orgUsers$,
      sortedComponents: sortedGraphComponents$,
      sortedChildrenByParent: sortedGraphComponents$.pipe(
        map(componentOperations.buildChildrenByParent)
      ),
      references: references$,
      locale: locale$,
      i18nLocale: i18nLocale$,
      referenceLabelFilterField: referenceLabelFilterField$,
      groupingRules: groupingRules$,
    }).subscribe(
      ({
        componentTypesByWorkspaceId,
        referenceTypesByWorkspaceId,
        fields,
        tags,
        workspaces,
        orgUsers,
        sortedComponents,
        sortedChildrenByParent,
        references,
        locale,
        i18nLocale,
        referenceLabelFilterField,
        groupingRules,
      }) => {
        this.sortedChildrenByParent = sortedChildrenByParent;
        this.groupingRules = groupingRuleOperations.getValidRules(
          groupingRules.groupingRules
        );
        this.graphModelData = {
          ...this.graphModelData,
          componentTypesByWorkspaceId,
          referenceTypesByWorkspaceId,
          fieldsByComponentType: fields.byComponentType,
          fieldsByReferenceType: fields.byReferenceType,
          workspacesById: workspaces.byId,
          orgUsersById: orgUsers.byId,
          allComponents: sortedComponents,
          allReferences: references.models,
          allTags: tags.models,
          allFields: fields.models,
          referenceLabelFilter: referenceLabelFilterField.referenceLabelFilter,
          labelField: referenceLabelFilterField.labelField,
          locale,
          i18nLocale,
        };
      }
    );

    this.unthrottledRebuild = this.rebuild;
  }
  rebuild = () => {
    // Garbage collect old compMap
    const transaction = profiling.startTransaction(
      'aggregated graph rebuild',
      500,
      profiling.Team.INSIGHT
    );
    this.compMap.clear();
    this.graphModelData = {
      ...this.graphModelData,
      ...getCurrentIncludedInContext(),
    };
    this.compMap = AggregatedGraphBuilder(
      this.rootNodeCollection,
      this.edgeMap,
      this.groupingRules,
      this.graphModelData
    );
    this.dirty = false;
    profiling.endTransaction(transaction);
  };

  rebuildIfDirty = () => {
    if (this.dirty) {
      this.rebuild();
    }
  };

  invalidateGraph = () => {
    this.dirty = true;
  };

  getFullGraph() {
    this.rebuildIfDirty();
    return {
      rootNodes: this.rootNodeCollection,
      edges: this.edgeMap,
      activeNodes: {},
    };
  }
  includeNodesAndEdges(
    nodes: GraphCollection<Node<GraphItemModel>>,
    {
      nodeSet,
      edgeSet,
      maxDegrees,
      maxDegreesIncoming,
      maxDegreesOutgoing,
      getAggregated,
      traverseLog,
    }: IncludeNodesAndEdgesParam
  ) {
    nodes.forEach(node => {
      GraphUtil.getGraphSubset({
        node,
        nodeSet,
        edgeSet,
        maxDegreesIncoming: maxDegreesIncoming ?? maxDegrees,
        maxDegreesOutgoing: maxDegreesOutgoing ?? maxDegrees,
        getAggregated,
        traverseLog,
      });
      this.includeNodesAndEdges(node.children, {
        nodeSet,
        edgeSet,
        maxDegrees,
        maxDegreesIncoming,
        maxDegreesOutgoing,
        getAggregated,
        traverseLog,
      });
    });
  }
  addChildGraphs({
    component,
    maxDegreesIncoming,
    maxDegreesOutgoing,
    isProcess,
    getAggregated,
    includeChildGraphs,
    includedNodes,
    includedEdges,
    traverseLog,
  }: AddChildGraphsArgs) {
    if (component) {
      (this.sortedChildrenByParent.get(component._id) ?? [])
        .filter(component =>
          this.graphModelData.includedComponentIds.has(component._id)
        )
        .forEach(childComponent => {
          const { edges, rootNodes } = this.getGraphByComponent({
            component: childComponent,
            maxDegreesIncoming,
            maxDegreesOutgoing,
            getAggregated,
            isProcess,
            includedNodes,
            includedEdges,
            includeChildGraphs,
            traverseLog,
          });

          rootNodes.forEach(node => includedNodes.add(node));
          edges.forEach(edge => includedEdges.add(edge));
          if (includeChildGraphs === ChildGraphInclusionPolicy.RECURSIVE) {
            this.addChildGraphs({
              component: childComponent,
              maxDegreesIncoming,
              maxDegreesOutgoing,
              isProcess,
              getAggregated,
              includeChildGraphs,
              includedNodes,
              includedEdges,
              traverseLog,
            });
          }
        });
    }
  }
  getGraphByWorkspace({
    workspaceId,
    maxDegrees,
    maxDegreesIncoming,
    maxDegreesOutgoing,
    isProcess = false,
    getAggregated = false,
    excludeRootNodesWithoutChildren = false,
    includeChildGraphs,
  }: AggregatedGraphByWorkspaceArgs) {
    const includedNodes = new Set<Node<GraphItemModel>>();
    const includedEdges = new Set<Edge>();

    if (!workspaceId) {
      return emptyGraph();
    }
    this.rebuildIfDirty();

    const workspaceComponents = this.graphModelData.workspacesById[workspaceId]
      ? this.graphModelData.allComponents.filter(
          component => component.rootWorkspace === workspaceId
        )
      : [];
    const rootComponentsOfWorkspace = workspaceComponents.filter(
      component =>
        !component.parent &&
        this.graphModelData.includedComponentIds.has(component._id)
    );
    const traverseLog = new Map();
    rootComponentsOfWorkspace.forEach(component => {
      const { rootNodes, edges } = this.getGraphByComponent({
        component,
        maxDegrees,
        maxDegreesIncoming,
        maxDegreesOutgoing,
        isProcess,
        getAggregated,
        includeChildGraphs,
        traverseLog,
      });

      rootNodes
        .filter(node => {
          if (excludeRootNodesWithoutChildren) return node.hasChildren();
          return true;
        })
        .forEach(node => includedNodes.add(node));
      edges.forEach(edge => includedEdges.add(edge));
    });

    GraphUtil.ensureAllEdgesAreIncluded({
      nodeSet: includedNodes,
      edgeSet: includedEdges,
      allEdges: this.edgeMap,
      getAggregated,
    });

    return {
      rootNodes: new NodeCollection(includedNodes),
      edges: new GraphCollection<Edge>(includedEdges),
      activeNodes: {},
    };
  }
  getGraphByComponent({
    component,
    isProcess = false,
    maxDegrees = 3,
    maxDegreesIncoming = maxDegrees,
    maxDegreesOutgoing = maxDegrees,
    getAggregated = false,
    includeChildGraphs = ChildGraphInclusionPolicy.NONE,
    includedNodes = new Set(),
    includedEdges = new Set(),
    traverseLog = new Map(),
  }: AggregatedGraphByComponentArgs) {
    this.rebuildIfDirty();
    const aggNode = component ? this.compMap.get(component._id) : undefined;
    const rootNodes = aggNode && aggNode.rootNodes;
    const activeNodes: Record<string, boolean> = {};
    const isContextRootNode = aggNode && aggNode.aggregators.length === 0;

    if (
      [
        ChildGraphInclusionPolicy.RECURSIVE,
        ChildGraphInclusionPolicy.ONE_LEVEL,
      ].includes(includeChildGraphs)
    ) {
      this.addChildGraphs({
        component,
        maxDegreesIncoming,
        maxDegreesOutgoing,
        getAggregated,
        includeChildGraphs,
        includedNodes,
        includedEdges,
        traverseLog,
      });
    }

    if (!component || !rootNodes) {
      // Make sure the component is included
      if (
        component &&
        !Array.from(includedNodes).find(
          node => node.isComponent() && node.dataModel._id === component._id
        )
      ) {
        includedNodes.add(
          createComponentNode({
            dataModel: component,
            getComponentTypeInfo: componentAttributes =>
              getComponentTypeInfoFor(componentAttributes, this.graphModelData),
          })
        );
      }
      return {
        rootNodes: new NodeCollection(includedNodes),
        edges: new GraphCollection<Edge>(includedEdges),
        activeNodes: {},
      };
    }

    // Add edges from actual context nodes
    if (!getAggregated) {
      aggNode.nodes.forEach(node => {
        if (isProcess) {
          GraphUtil.getOutgoingGraphSubset({
            node,
            nodeSet: includedNodes,
            edgeSet: includedEdges,
            maxDegrees: maxDegreesOutgoing,
            getAggregated,
            traverseLog,
          });
          // Get incoming nodes on the outgoing subset
          const incomingNodes = new Set<Node<GraphItemModel>>();
          includedNodes.forEach(node => {
            GraphUtil.getIncomingGraphSubset({
              node,
              nodeSet: incomingNodes,
              edgeSet: includedEdges,
              maxDegrees: ensureAtLeastOneDegreeForNodeInContext({
                aggNode,
                node,
                maxDegrees: maxDegreesIncoming,
              }),
              traverseLog,
            });
          });
          incomingNodes.forEach(node => includedNodes.add(node));
        } else {
          GraphUtil.getGraphSubset({
            node,
            nodeSet: includedNodes,
            edgeSet: includedEdges,
            maxDegreesIncoming: maxDegreesIncoming ?? maxDegrees,
            maxDegreesOutgoing: maxDegreesOutgoing ?? maxDegrees,
            getAggregated,
            traverseLog,
          });
        }
      });
    }

    // Add edges from aggregated root nodes
    rootNodes.forEach(rootNode => {
      activeNodes[rootNode.id] = true;
      if (getAggregated) {
        GraphUtil.getGraphSubset({
          node: rootNode,
          nodeSet: includedNodes,
          edgeSet: includedEdges,
          maxDegreesIncoming,
          maxDegreesOutgoing,
          getAggregated,
          traverseLog,
        });
      } else if (isContextRootNode) {
        // Include edges from children of rootNode (recursively)
        this.includeNodesAndEdges(rootNode.children, {
          nodeSet: includedNodes,
          edgeSet: includedEdges,
          maxDegreesIncoming,
          maxDegreesOutgoing,
          getAggregated,
          traverseLog,
        });
      }
    });

    // Add parents of 'lonely' children
    includedNodes.forEach(function (node) {
      if (node.parent && !includedNodes.has(node.parent)) {
        includeNodeAndParents(includedNodes, node.parent);
      }
    });

    GraphUtil.ensureAllEdgesAreIncluded({
      nodeSet: includedNodes,
      edgeSet: includedEdges,
      allEdges: this.edgeMap,
      getAggregated,
    });

    if (getAggregated) {
      const edgesToDelete: Edge[] = [];

      includedEdges.forEach(function (edge) {
        if (edge.aggregatedTarget === edge.aggregatedSource) {
          // Remove edges within aggregators
          edgesToDelete.push(edge);
        }
      });
      edgesToDelete.forEach(function (edge) {
        includedEdges.delete(edge);
      });
    }

    return {
      rootNodes: new NodeCollection(includedNodes),
      edges: new GraphCollection<Edge>(includedEdges),
      activeNodes: activeNodes,
    };
  }
}

/** Built on first use, for the reason `getHierarchicGraph` is. */
export const getAggregatedGraph: () => AggregatedGraph = once(
  () => new AggregatedGraph()
);
